import { CAPITAL_CASH_BALANCE_SHEET_PUBLIC_FILING_SOURCE_SYSTEM_REF } from './approved-source-systems';

export const CAPITAL_CASH_MODULE_ID = 'capital-cash' as const;
export const CAPITAL_CASH_BALANCE_SHEET_SNAPSHOT_SCHEMA_VERSION =
  'capital_cash_balance_sheet_snapshot_v1' as const;
export const CAPITAL_CASH_COMPANY_COA_MAPPING_SCHEMA_VERSION =
  'capital_cash_company_coa_mapping_v1' as const;

export const CAPITAL_CASH_BALANCE_SHEET_METRIC_KEYS = Object.freeze([
  'cash_and_cash_equivalents_usd',
  'accounts_receivable_usd',
  'inventory_usd',
  'accounts_payable_usd',
  'total_current_assets_usd',
  'total_current_liabilities_usd',
  'capital_expenditures_usd',
  'days_sales_outstanding_days',
  'days_inventory_outstanding_days',
  'days_payables_outstanding_days',
  'cash_conversion_cycle_days',
] as const);

export const CAPITAL_CASH_WORKING_CAPITAL_COMPONENT_KEYS = Object.freeze([
  'days_sales_outstanding',
  'days_inventory_outstanding',
  'days_payables_outstanding',
] as const);

export const CAPITAL_CASH_COMPANY_COA_MAPPING_REQUIRED_ROLES = Object.freeze([
  'accounts_receivable',
  'accounts_payable',
  'inventory',
  'revenue',
  'cost_of_goods_sold',
] as const);

export type CapitalCashBalanceSheetMetricKey =
  (typeof CAPITAL_CASH_BALANCE_SHEET_METRIC_KEYS)[number];
export type CapitalCashWorkingCapitalComponentKey =
  (typeof CAPITAL_CASH_WORKING_CAPITAL_COMPONENT_KEYS)[number];
export type CapitalCashCompanyCoaMappingRole =
  (typeof CAPITAL_CASH_COMPANY_COA_MAPPING_REQUIRED_ROLES)[number];

export type CompanyCoaMappingLine = Readonly<{
  role: CapitalCashCompanyCoaMappingRole;
  statement: 'balance_sheet' | 'income_statement';
  sourceLineKeys: readonly string[];
  required: true;
  description: string;
}>;

export type CompanyCoaMapping = Readonly<{
  schemaVersion: typeof CAPITAL_CASH_COMPANY_COA_MAPPING_SCHEMA_VERSION;
  mappingId: string;
  tenantId: string;
  companyId: string;
  industryProfileId: string;
  sourceAsOf: string;
  fixtureOnly?: boolean;
  lines: readonly CompanyCoaMappingLine[];
}>;

export type CapitalCashCompanyCoaFact = Readonly<{
  tenantId: string;
  companyId: string;
  industryProfileId: string;
  mappingId: string;
  sourceLineKey: string;
  amount: number;
  unit: 'USD';
  periodKey: string;
  sourceAsOf: string;
  artifactId: string;
  ingestJobId: string;
  sourceRef: string;
  sourceSystemRef: typeof CAPITAL_CASH_BALANCE_SHEET_PUBLIC_FILING_SOURCE_SYSTEM_REF;
  evidenceSchemaVersion: typeof CAPITAL_CASH_BALANCE_SHEET_SNAPSHOT_SCHEMA_VERSION;
  evidenceRefId: string;
}>;

export type CompanyCoaMappingValidationErrorCode =
  | 'invalid_mapping_shape'
  | 'missing_required_mapping_line'
  | 'empty_required_mapping_source_lines'
  | 'duplicate_mapping_role'
  | 'duplicate_source_line_mapping'
  | 'mapped_source_line_missing'
  | 'unmapped_required_source_line';

export type CompanyCoaMappingValidationError = Readonly<{
  code: CompanyCoaMappingValidationErrorCode;
  path: string;
  message: string;
  role?: CapitalCashCompanyCoaMappingRole;
  sourceLineKey?: string;
}>;

export type CompanyCoaMappingValidationResult = Readonly<{
  valid: boolean;
  errors: readonly CompanyCoaMappingValidationError[];
}>;

export type ValidateCompanyCoaMappingOptions = Readonly<{
  availableSourceLineKeys?: readonly string[];
  requiredSourceLineKeys?: readonly string[];
}>;

export type CapitalCashFactGrain = Readonly<{
  businessLineKey: string;
  geoKey: string;
  customerKey: string;
  laneKey: string;
  siteKey: string;
}>;

export type CapitalCashSourceMetadata = Readonly<{
  sourceSystemRef: typeof CAPITAL_CASH_BALANCE_SHEET_PUBLIC_FILING_SOURCE_SYSTEM_REF;
  sourceClass: 'public-filing';
  sourceRef: string;
  sourceAsOf: string;
  approvedAt: string;
  artifactId: string;
  ingestJobId: string;
}>;

export type CapitalCashBalanceSheetRecord = Readonly<{
  lineKey: CapitalCashBalanceSheetMetricKey;
  metricKey: CapitalCashBalanceSheetMetricKey;
  label: string;
  amount: number;
  unit: 'USD' | 'days';
  statement: 'balance_sheet' | 'cash_flow' | 'derived_working_capital';
  fiscalYear: number;
  periodType: string;
  periodKey: string;
  scopeKind: string;
  scopeId: string;
  scopeLabel: string;
  grain: CapitalCashFactGrain;
  sourceAsOf: string;
  evidenceRefId: string;
  sourceObjectId: string;
}>;

export type CapitalCashWorkingCapitalComponent = Readonly<{
  componentKey: CapitalCashWorkingCapitalComponentKey;
  metricKey: Extract<
    CapitalCashBalanceSheetMetricKey,
    | 'days_sales_outstanding_days'
    | 'days_inventory_outstanding_days'
    | 'days_payables_outstanding_days'
  >;
  value: number;
  unit: 'days';
  fiscalYear: number;
  periodType: string;
  periodKey: string;
  scopeKind: string;
  scopeId: string;
  scopeLabel: string;
  numeratorMetricKey: CapitalCashBalanceSheetMetricKey | 'revenue_usd' | 'cost_of_revenue_usd';
  denominatorMetricKey: CapitalCashBalanceSheetMetricKey | 'revenue_usd' | 'cost_of_revenue_usd';
  sourceAsOf: string;
  evidenceRefId: string;
  formulaRef: string;
}>;

export type CapitalCashCapexLine = Readonly<{
  lineKey: 'capital_expenditures_usd';
  metricKey: 'capital_expenditures_usd';
  label: string;
  amount: number;
  unit: 'USD';
  fiscalYear: number;
  periodType: string;
  periodKey: string;
  scopeKind: string;
  scopeId: string;
  scopeLabel: string;
  sourceAsOf: string;
  evidenceRefId: string;
  sourceObjectId: string;
}>;

export type CapitalCashBalanceSheetSnapshotArtifact = Readonly<{
  schemaVersion: typeof CAPITAL_CASH_BALANCE_SHEET_SNAPSHOT_SCHEMA_VERSION;
  moduleId: typeof CAPITAL_CASH_MODULE_ID;
  tenantId: string;
  companyId: string;
  industryProfileId: string;
  companyMappingId: string;
  fixtureOnly?: boolean;
  source: CapitalCashSourceMetadata;
  records: readonly CapitalCashBalanceSheetRecord[];
  workingCapitalComponents: readonly CapitalCashWorkingCapitalComponent[];
  capexLines: readonly CapitalCashCapexLine[];
}>;

export type CapitalCashValidationResult = Readonly<{
  valid: boolean;
  errors: readonly string[];
}>;

export const CAPITAL_CASH_3PL_COMPANY_COA_MAPPING_FIXTURE: CompanyCoaMapping = Object.freeze({
  schemaVersion: CAPITAL_CASH_COMPANY_COA_MAPPING_SCHEMA_VERSION,
  mappingId: '3pl-capital-cash-coa-fixture-v1',
  tenantId: 'tenant_test',
  companyId: 'chrw',
  industryProfileId: '3pl-logistics',
  sourceAsOf: '2025-12-31T00:00:00.000Z',
  fixtureOnly: true,
  lines: Object.freeze([
    Object.freeze({
      role: 'accounts_receivable',
      statement: 'balance_sheet',
      sourceLineKeys: Object.freeze(['trade_accounts_receivable_net']),
      required: true,
      description: 'Heavy customer receivables balance typical for freight brokerage billing.',
    }),
    Object.freeze({
      role: 'accounts_payable',
      statement: 'balance_sheet',
      sourceLineKeys: Object.freeze(['carrier_accounts_payable']),
      required: true,
      description: 'Carrier and purchased transportation payable balance.',
    }),
    Object.freeze({
      role: 'inventory',
      statement: 'balance_sheet',
      sourceLineKeys: Object.freeze(['warehouse_supplies_inventory']),
      required: true,
      description: 'Minimal inventory line for 3PL supplies and owned warehouse consumables.',
    }),
    Object.freeze({
      role: 'revenue',
      statement: 'income_statement',
      sourceLineKeys: Object.freeze(['freight_revenue']),
      required: true,
      description: 'Freight and logistics revenue denominator for DSO.',
    }),
    Object.freeze({
      role: 'cost_of_goods_sold',
      statement: 'income_statement',
      sourceLineKeys: Object.freeze(['purchased_transportation_cogs']),
      required: true,
      description: 'Purchased transportation cost denominator for DIO and DPO.',
    }),
  ]),
});

export function isCapitalCashBalanceSheetMetricKey(
  value: unknown,
): value is CapitalCashBalanceSheetMetricKey {
  return (
    typeof value === 'string' &&
    (CAPITAL_CASH_BALANCE_SHEET_METRIC_KEYS as readonly string[]).includes(value)
  );
}

export function isCapitalCashWorkingCapitalComponentKey(
  value: unknown,
): value is CapitalCashWorkingCapitalComponentKey {
  return (
    typeof value === 'string' &&
    (CAPITAL_CASH_WORKING_CAPITAL_COMPONENT_KEYS as readonly string[]).includes(value)
  );
}

export function isCapitalCashCompanyCoaMappingRole(
  value: unknown,
): value is CapitalCashCompanyCoaMappingRole {
  return (
    typeof value === 'string' &&
    (CAPITAL_CASH_COMPANY_COA_MAPPING_REQUIRED_ROLES as readonly string[]).includes(value)
  );
}

export function validateCompanyCoaMapping(
  mapping: unknown,
  options: ValidateCompanyCoaMappingOptions = {},
): CompanyCoaMappingValidationResult {
  const errors: CompanyCoaMappingValidationError[] = [];
  const record = asRecord(mapping);
  if (!record) {
    return {
      valid: false,
      errors: [
        {
          code: 'invalid_mapping_shape',
          path: 'mapping',
          message: 'Company COA mapping must be an object.',
        },
      ],
    };
  }

  if (record.schemaVersion !== CAPITAL_CASH_COMPANY_COA_MAPPING_SCHEMA_VERSION) {
    errors.push({
      code: 'invalid_mapping_shape',
      path: 'schemaVersion',
      message: `schemaVersion must be ${CAPITAL_CASH_COMPANY_COA_MAPPING_SCHEMA_VERSION}`,
    });
  }
  for (const field of ['mappingId', 'tenantId', 'companyId', 'industryProfileId', 'sourceAsOf']) {
    if (typeof record[field] !== 'string' || String(record[field]).trim().length === 0) {
      errors.push({
        code: 'invalid_mapping_shape',
        path: field,
        message: `${field} must be a non-empty string.`,
      });
    }
  }

  const lineRecords = Array.isArray(record.lines) ? record.lines : null;
  if (!lineRecords) {
    errors.push({
      code: 'invalid_mapping_shape',
      path: 'lines',
      message: 'lines must be an array.',
    });
    return { valid: false, errors };
  }

  const seenRoles = new Set<CapitalCashCompanyCoaMappingRole>();
  const mappedSourceLineKeys = new Set<string>();
  lineRecords.forEach((line, index) => {
    const row = asRecord(line);
    if (!row) {
      errors.push({
        code: 'invalid_mapping_shape',
        path: `lines[${index}]`,
        message: `lines[${index}] must be an object.`,
      });
      return;
    }
    if (!isCapitalCashCompanyCoaMappingRole(row.role)) {
      errors.push({
        code: 'invalid_mapping_shape',
        path: `lines[${index}].role`,
        message: `lines[${index}].role must be a capital-cash COA mapping role.`,
      });
      return;
    }
    const role = row.role;
    if (seenRoles.has(role)) {
      errors.push({
        code: 'duplicate_mapping_role',
        path: `lines.${role}`,
        message: `COA mapping role ${role} is declared more than once.`,
        role,
      });
    }
    seenRoles.add(role);

    const expectedStatement = expectedCompanyCoaMappingStatement(role);
    if (row.statement !== 'balance_sheet' && row.statement !== 'income_statement') {
      errors.push({
        code: 'invalid_mapping_shape',
        path: `lines.${role}.statement`,
        message: `COA mapping role ${role} must declare a valid statement.`,
        role,
      });
    } else if (row.statement !== expectedStatement) {
      errors.push({
        code: 'invalid_mapping_shape',
        path: `lines.${role}.statement`,
        message: `COA mapping role ${role} must map to ${expectedStatement}.`,
        role,
      });
    }

    if (row.required !== true) {
      errors.push({
        code: 'invalid_mapping_shape',
        path: `lines.${role}.required`,
        message: `COA mapping role ${role} must be required.`,
        role,
      });
    }

    if (typeof row.description !== 'string' || row.description.trim().length === 0) {
      errors.push({
        code: 'invalid_mapping_shape',
        path: `lines.${role}.description`,
        message: `COA mapping role ${role} must include a non-empty description.`,
        role,
      });
    }

    const sourceLineKeys: string[] = [];
    if (!Array.isArray(row.sourceLineKeys)) {
      errors.push({
        code: 'invalid_mapping_shape',
        path: `lines.${role}.sourceLineKeys`,
        message: `COA mapping role ${role} sourceLineKeys must be an array.`,
        role,
      });
    } else {
      row.sourceLineKeys.forEach((value, sourceLineIndex) => {
        if (typeof value !== 'string' || value.trim().length === 0) {
          errors.push({
            code: 'invalid_mapping_shape',
            path: `lines.${role}.sourceLineKeys[${sourceLineIndex}]`,
            message: `COA mapping role ${role} sourceLineKeys must be non-empty strings.`,
            role,
          });
          return;
        }
        sourceLineKeys.push(value);
      });
    }

    if (sourceLineKeys.length === 0) {
      errors.push({
        code: 'empty_required_mapping_source_lines',
        path: `lines.${role}.sourceLineKeys`,
        message: `COA mapping role ${role} must name at least one source line key.`,
        role,
      });
      return;
    }
    for (const sourceLineKey of sourceLineKeys) {
      if (mappedSourceLineKeys.has(sourceLineKey)) {
        errors.push({
          code: 'duplicate_source_line_mapping',
          path: `lines.${role}.sourceLineKeys`,
          message: `Source line ${sourceLineKey} is mapped by more than one role.`,
          role,
          sourceLineKey,
        });
      }
      mappedSourceLineKeys.add(sourceLineKey);
    }
  });

  for (const role of CAPITAL_CASH_COMPANY_COA_MAPPING_REQUIRED_ROLES) {
    if (!seenRoles.has(role)) {
      errors.push({
        code: 'missing_required_mapping_line',
        path: `lines.${role}`,
        message: `Required COA mapping role ${role} is missing.`,
        role,
      });
    }
  }

  const availableSourceLineKeys = new Set(options.availableSourceLineKeys ?? []);
  if (availableSourceLineKeys.size > 0) {
    for (const sourceLineKey of mappedSourceLineKeys) {
      if (!availableSourceLineKeys.has(sourceLineKey)) {
        errors.push({
          code: 'mapped_source_line_missing',
          path: 'lines.sourceLineKeys',
          message: `Mapped source line ${sourceLineKey} is not present in available facts.`,
          sourceLineKey,
        });
      }
    }
  }

  for (const sourceLineKey of options.requiredSourceLineKeys ?? []) {
    if (!mappedSourceLineKeys.has(sourceLineKey)) {
      errors.push({
        code: 'unmapped_required_source_line',
        path: 'requiredSourceLineKeys',
        message: `Required source line ${sourceLineKey} is not mapped to a capital-cash role.`,
        sourceLineKey,
      });
    }
  }

  return { valid: errors.length === 0, errors };
}

function expectedCompanyCoaMappingStatement(
  role: CapitalCashCompanyCoaMappingRole,
): CompanyCoaMappingLine['statement'] {
  if (role === 'revenue' || role === 'cost_of_goods_sold') return 'income_statement';
  return 'balance_sheet';
}

export function validateCapitalCashBalanceSheetSnapshotArtifact(
  artifact: unknown,
): CapitalCashValidationResult {
  const errors: string[] = [];
  const record = asRecord(artifact);
  if (!record) {
    return { valid: false, errors: ['Artifact must be an object'] };
  }

  requireLiteral(
    record.schemaVersion,
    CAPITAL_CASH_BALANCE_SHEET_SNAPSHOT_SCHEMA_VERSION,
    'schemaVersion',
    errors,
  );
  requireLiteral(record.moduleId, CAPITAL_CASH_MODULE_ID, 'moduleId', errors);
  requireNonBlank(record.tenantId, 'tenantId', errors);
  requireNonBlank(record.companyId, 'companyId', errors);
  requireNonBlank(record.industryProfileId, 'industryProfileId', errors);
  requireNonBlank(record.companyMappingId, 'companyMappingId', errors);
  validateSource(record.source, errors);
  validateRecords(record.records, errors);
  validateWorkingCapitalComponents(record.workingCapitalComponents, errors);
  validateCapexLines(record.capexLines, errors);

  return { valid: errors.length === 0, errors };
}

function validateSource(value: unknown, errors: string[]): void {
  const source = asRecord(value);
  if (!source) {
    errors.push('source must be an object');
    return;
  }
  requireLiteral(
    source.sourceSystemRef,
    CAPITAL_CASH_BALANCE_SHEET_PUBLIC_FILING_SOURCE_SYSTEM_REF,
    'source.sourceSystemRef',
    errors,
  );
  requireLiteral(source.sourceClass, 'public-filing', 'source.sourceClass', errors);
  requireNonBlank(source.sourceRef, 'source.sourceRef', errors);
  requireIsoDate(source.sourceAsOf, 'source.sourceAsOf', errors);
  requireIsoDate(source.approvedAt, 'source.approvedAt', errors);
  requireNonBlank(source.artifactId, 'source.artifactId', errors);
  requireNonBlank(source.ingestJobId, 'source.ingestJobId', errors);
}

function validateRecords(value: unknown, errors: string[]): void {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push('records must be a non-empty array');
    return;
  }
  value.forEach((item, index) => {
    const row = asRecord(item);
    if (!row) {
      errors.push(`records[${index}] must be an object`);
      return;
    }
    if (!isCapitalCashBalanceSheetMetricKey(row.lineKey)) {
      errors.push(`records[${index}].lineKey must be a capital-cash metric key`);
    }
    if (!isCapitalCashBalanceSheetMetricKey(row.metricKey)) {
      errors.push(`records[${index}].metricKey must be a capital-cash metric key`);
    }
    if (row.lineKey !== row.metricKey) {
      errors.push(`records[${index}].lineKey must equal metricKey`);
    }
    requireNonBlank(row.label, `records[${index}].label`, errors);
    requireFiniteNumber(row.amount, `records[${index}].amount`, errors);
    requireNonBlank(row.unit, `records[${index}].unit`, errors);
    requireNonBlank(row.statement, `records[${index}].statement`, errors);
    requireInteger(row.fiscalYear, `records[${index}].fiscalYear`, errors);
    requireNonBlank(row.periodType, `records[${index}].periodType`, errors);
    requireNonBlank(row.periodKey, `records[${index}].periodKey`, errors);
    requireNonBlank(row.scopeKind, `records[${index}].scopeKind`, errors);
    requireNonBlank(row.scopeId, `records[${index}].scopeId`, errors);
    requireNonBlank(row.scopeLabel, `records[${index}].scopeLabel`, errors);
    validateGrain(row.grain, `records[${index}].grain`, errors);
    requireIsoDate(row.sourceAsOf, `records[${index}].sourceAsOf`, errors);
    requireNonBlank(row.evidenceRefId, `records[${index}].evidenceRefId`, errors);
    requireNonBlank(row.sourceObjectId, `records[${index}].sourceObjectId`, errors);
  });
}

function validateWorkingCapitalComponents(value: unknown, errors: string[]): void {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push('workingCapitalComponents must be a non-empty array');
    return;
  }
  value.forEach((item, index) => {
    const row = asRecord(item);
    if (!row) {
      errors.push(`workingCapitalComponents[${index}] must be an object`);
      return;
    }
    if (!isCapitalCashWorkingCapitalComponentKey(row.componentKey)) {
      errors.push(`workingCapitalComponents[${index}].componentKey is invalid`);
    }
    if (!isCapitalCashBalanceSheetMetricKey(row.metricKey)) {
      errors.push(`workingCapitalComponents[${index}].metricKey is invalid`);
    }
    requireFiniteNumber(row.value, `workingCapitalComponents[${index}].value`, errors);
    requireLiteral(row.unit, 'days', `workingCapitalComponents[${index}].unit`, errors);
    requireInteger(row.fiscalYear, `workingCapitalComponents[${index}].fiscalYear`, errors);
    requireNonBlank(row.periodType, `workingCapitalComponents[${index}].periodType`, errors);
    requireNonBlank(row.periodKey, `workingCapitalComponents[${index}].periodKey`, errors);
    requireNonBlank(row.scopeKind, `workingCapitalComponents[${index}].scopeKind`, errors);
    requireNonBlank(row.scopeId, `workingCapitalComponents[${index}].scopeId`, errors);
    requireNonBlank(row.scopeLabel, `workingCapitalComponents[${index}].scopeLabel`, errors);
    requireNonBlank(
      row.numeratorMetricKey,
      `workingCapitalComponents[${index}].numeratorMetricKey`,
      errors,
    );
    requireNonBlank(
      row.denominatorMetricKey,
      `workingCapitalComponents[${index}].denominatorMetricKey`,
      errors,
    );
    requireIsoDate(row.sourceAsOf, `workingCapitalComponents[${index}].sourceAsOf`, errors);
    requireNonBlank(row.evidenceRefId, `workingCapitalComponents[${index}].evidenceRefId`, errors);
    requireNonBlank(row.formulaRef, `workingCapitalComponents[${index}].formulaRef`, errors);
  });
}

function validateCapexLines(value: unknown, errors: string[]): void {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push('capexLines must be a non-empty array');
    return;
  }
  value.forEach((item, index) => {
    const row = asRecord(item);
    if (!row) {
      errors.push(`capexLines[${index}] must be an object`);
      return;
    }
    requireLiteral(row.lineKey, 'capital_expenditures_usd', `capexLines[${index}].lineKey`, errors);
    requireLiteral(
      row.metricKey,
      'capital_expenditures_usd',
      `capexLines[${index}].metricKey`,
      errors,
    );
    requireNonBlank(row.label, `capexLines[${index}].label`, errors);
    requireFiniteNumber(row.amount, `capexLines[${index}].amount`, errors);
    requireLiteral(row.unit, 'USD', `capexLines[${index}].unit`, errors);
    requireInteger(row.fiscalYear, `capexLines[${index}].fiscalYear`, errors);
    requireNonBlank(row.periodType, `capexLines[${index}].periodType`, errors);
    requireNonBlank(row.periodKey, `capexLines[${index}].periodKey`, errors);
    requireNonBlank(row.scopeKind, `capexLines[${index}].scopeKind`, errors);
    requireNonBlank(row.scopeId, `capexLines[${index}].scopeId`, errors);
    requireNonBlank(row.scopeLabel, `capexLines[${index}].scopeLabel`, errors);
    requireIsoDate(row.sourceAsOf, `capexLines[${index}].sourceAsOf`, errors);
    requireNonBlank(row.evidenceRefId, `capexLines[${index}].evidenceRefId`, errors);
    requireNonBlank(row.sourceObjectId, `capexLines[${index}].sourceObjectId`, errors);
  });
}

function validateGrain(value: unknown, path: string, errors: string[]): void {
  const grain = asRecord(value);
  if (!grain) {
    errors.push(`${path} must be an object`);
    return;
  }
  requireNonBlank(grain.businessLineKey, `${path}.businessLineKey`, errors);
  requireNonBlank(grain.geoKey, `${path}.geoKey`, errors);
  requireNonBlank(grain.customerKey, `${path}.customerKey`, errors);
  requireNonBlank(grain.laneKey, `${path}.laneKey`, errors);
  requireNonBlank(grain.siteKey, `${path}.siteKey`, errors);
}

function requireLiteral(
  value: unknown,
  expected: string,
  path: string,
  errors: string[],
): void {
  if (value !== expected) errors.push(`${path} must be ${expected}`);
}

function requireNonBlank(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${path} must be a non-empty string`);
  }
}

function requireFiniteNumber(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    errors.push(`${path} must be a finite number`);
  }
}

function requireInteger(value: unknown, path: string, errors: string[]): void {
  if (!Number.isInteger(value)) {
    errors.push(`${path} must be an integer`);
  }
}

function requireIsoDate(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) {
    errors.push(`${path} must be an ISO timestamp`);
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}
